import { atom, RecoilState, selector } from "recoil";

import { rollbar, RollbarLogArgument } from "@utils/rollbar";

import { Quote, ResultDTO } from "@organisms/result/domain/result";
import { resultState } from "@organisms/result/state/atoms";

import { Product } from "@features/quotes-funnel/domain/product";
import { Coupon, PromotionCodeAttrs } from "@features/quotes-funnel/domain/promotion-code";
import { ShoppingCart, ShoppingCartDTO } from "@features/quotes-funnel/domain/shopping-cart";
import { PaymentIntervalMonths } from "@features/quotes-funnel/settings/payments";
import { promotionCodeState } from "@features/quotes-funnel/state/promotion-code-state";

const getDefaultShoppingCartState = (
  quotes: Quote[],
  paymentIntervalMonths: PaymentIntervalMonths,
  coupon: Coupon | undefined,
  currency: string
) => {
  const products = quotes.map((quote) => {
    let selected: boolean;

    try {
      selected = ShoppingCart.isProductSelectedByDefault(quotes, quote.product.id);
    } catch (error) {
      rollbar.warn(
        "Unexpected product id when determining default selection state" as RollbarLogArgument
      );
      selected = false;
    }

    return new Product({
      id: quote.product.id,
      name: quote.product.name,
      selected,
      price: quote.premium,
    });
  });

  return { products, paymentIntervalMonths, coupon, currency } as ShoppingCartDTO;
};

const emptyCart = (): ShoppingCartDTO => {
  return {
    products: [],
    paymentIntervalMonths: PaymentIntervalMonths.monthly,
    coupon: undefined,
    currency: "eur",
  } as ShoppingCartDTO;
};

export const shoppingCartSelector: RecoilState<ShoppingCartDTO> = selector<ShoppingCartDTO>({
  key: "shoppingCartSelector",
  get: ({ get }) => {
    const result: ResultDTO | null = get(resultState);
    const promotionCode: PromotionCodeAttrs | null = get(promotionCodeState);

    if (!result) {
      return emptyCart();
    }

    const quotes: Quote[] = result.quotes;
    const coupon: Coupon | undefined = promotionCode?.coupon ?? undefined;
    const currency: string = result.currency;
    const paymentIntervalMonths: PaymentIntervalMonths = result.payment_interval_months;

    try {
      return getDefaultShoppingCartState(quotes, paymentIntervalMonths, coupon, currency);
    } catch (error) {
      rollbar.error(
        "There was an error getting the default shopping cart state" as RollbarLogArgument
      );

      return emptyCart();
    }
  },
  set: ({ set }, newValue: ShoppingCartDTO) => {
    set(shoppingCartState, newValue);
  },
});

export const shoppingCartState: RecoilState<ShoppingCartDTO> = atom({
  key: "shoppingCart",
  default: shoppingCartSelector,
});

export const shoppingCartSummaryStuckState: RecoilState<boolean> = atom<boolean>({
  key: "shoppingCartSummaryStuck",
  default: false,
});

export const alternativeShoppingCartSummaryVisibleState: RecoilState<boolean> = atom<boolean>({
  key: "alternativeShoppingCartSummaryVisible",
  default: false,
});
